/**
 * Page-level constants + display helpers for Premium Selection.
 * The calculation types/defaults live in `domain/pricing` — this file
 * owns what's UI-only: criteria checklists, SIR presets, formatting.
 */

// ─────────────────────────── Presets ──────────────────────────────

/** Retention levels offered by the SIR dropdown (Figma). */
export const SIR_PRESETS = [25000, 50000, 75000, 100000, 150000, 200000, 250000];

/** Quality credit dropdown — 15% quality / 40% super credit, one at a
 *  time, plus custom (Henry's condensed-dropdown design decision). */
/** Policy Terms dropdown presets — Dario's most-common values (OR-1331,
 *  confirmed in OR-1330's design comment). Custom entry stays available. */
export const PER_OCCURRENCE_PRESETS = [500000, 1000000, 1500000];
export const POLICY_AGGREGATE_PRESETS = [3000000, 6000000, 10000000];

/** Coverage B / C dec sub-limits (OR-1455). The single value in each list
 *  is the standard amount from Andressa's mock, which mirrors the old
 *  Limits of Liability modal; Custom covers the rest. Still pending UW
 *  confirmation — her open question 4 also asks whether the three
 *  evacuation figures should differ, since the old modal had one. */
export const FIRE_DAMAGE_AGGREGATE_PRESETS = [100000];
export const GL_EACH_CLAIM_DEDUCTIBLE_PRESETS = [5000];
export const MEDICAL_EXPENSE_PRESETS = [5000];
export const EVACUATION_PRESETS = [25000];

export const QUALITY_CREDIT_PRESETS = [
    { id: "15", label: "15%" },
    { id: "40", label: "40%" },
];

// ──────────────────────── Criteria lists ──────────────────────────

export interface CriterionItem {
    id: string;
    label: string;
}

/** 40% super-credit qualifying criteria — UW-checked, none default. */
// Order matches the pricing-model spreadsheet (Andressa, jul-22). Persistence
// is by id → enum (FORTY_PCT_TO_ENUM), so this order is display-only.
export const FORTY_PCT_CRITERIA: readonly CriterionItem[] = [
    { id: "above-surveys", label: "Above State/Nat'l Surveys" },
    { id: "top-25-metrics", label: "Top 25% for Metrics" },
    { id: "good-aesthetic", label: "Good Aesthetic" },
    { id: "good-social", label: "Good Social Activities" },
    { id: "loss-ratio-25-deductible", label: "5 years 25% loss Ratio adjusted for Deductible" },
    { id: "loss-ratio-15", label: "15% loss Ratio for Accounts with 5 years of data" },
    { id: "no-jeopardy-tags", label: "No immediate jeopardy Tags for past 2 years" },
    { id: "no-sff-abuse", label: "No SFF/Abuse Claims for previous 1 Year" },
    { id: "no-100k-claims", label: "No More than 2 $100k claims in previous 5 years" },
];

/** Referral-to-capital criteria. Will be pre-filled from account data
 *  once the backend exposes the flags — TODO(confirm logic with dev
 *  team, see Figma "Referral Criteria auto-fill logic" open question). */
export const REFERRAL_CRITERIA: readonly CriterionItem[] = [
    { id: "premiums-above-1m", label: "Premiums above $1M" },
    { id: "incurred-lr-40", label: "5-year incurred loss Ratio greater than 40%" },
    { id: "single-claim-500k", label: "Single claim in the past 3-years greater than $500k" },
    { id: "deductible-above-250k", label: "Deductible above $250k" },
    { id: "sexual-assault-claims", label: "Sexual Assault claims w/ Incurred Indemnity" },
    { id: "renewal-lr-over-50", label: "Any Renewal with a loss ratio over 50% to Accelerant" },
    { id: "premium-adjustment-higher-lr", label: "Adjustment of Premium post renewal which results in a higher Loss Ratio than approved." },
    { id: "new-corridor-buffer", label: "New Corridor or Buffer Policies" },
];

export function emptyCriteriaState(items: readonly CriterionItem[]): Record<string, boolean> {
    return Object.fromEntries(items.map((i) => [i.id, false]));
}

// ──────────────────────── Display helpers ─────────────────────────

const EMPTY = "—";

/** "$1,172,485" for whole dollars, "$726,087.50" when there are cents
 *  (the Figma shows both treatments). null → em dash. */
export function displayMoneyValue(n: number | null): string {
    if (n === null || !Number.isFinite(n)) return EMPTY;
    const hasCents = Math.abs(n % 1) > 1e-9;
    return `$${n.toLocaleString("en-US", {
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
    })}`;
}

/** 0–1 fraction → whole percent ("0.675957" → "68%"), rounded like the
 *  Loss Runs summary cards so ratio metrics read the same across the app
 *  (QA follow-up on OR-1241 — the full-wire-precision display diverged
 *  from every other percent on screen). */
export function displayPercent(fraction: number | null): string {
    if (fraction === null || !Number.isFinite(fraction)) return EMPTY;
    return `${Math.round(fraction * 100).toLocaleString("en-US")}%`;
}

/** Format a money INPUT string ("726087.50" → "$726,087.50") for
 *  read-only display (the top summary cards echo the Selected Premium /
 *  SIR the underwriter typed). Empty / garbage → em dash. */
export function displayMoneyInput(raw: string): string {
    if (raw.trim() === "") return EMPTY;
    const n = Number(raw.replace(/,/g, ""));
    return displayMoneyValue(Number.isFinite(n) ? n : null);
}

/** Whole-percent number ("25" → "25%"). */
export function displayPctNumber(pct: number | null): string {
    if (pct === null || !Number.isFinite(pct)) return EMPTY;
    return `${Number(pct.toFixed(2))}%`;
}

/** Clamp a whole-percent input to the wire-legal range. Quote rates are
 *  Numeric(8,6) FRACTIONS — at most 2 digits before the decimal point —
 *  so the whole-percent input tops out at 9,999.99% (the PUT 422s past
 *  it). Empty / non-numeric input passes through untouched. */
export function clampPctToWire(raw: string): string {
    if (raw.trim() === "") return raw;
    const n = Number(raw.replace(/,/g, ""));
    if (!Number.isFinite(n)) return raw;
    const clamped = Math.min(9999.99, Math.max(0, n));
    return clamped === n ? raw : String(clamped);
}

/** Clamp a dollar input to the wire-legal range. Quote money fields are
 *  Numeric(14,2) — at most 12 digits before the decimal point — so the
 *  input tops out at 999,999,999,999.99 (the PUT 422s past it). Empty /
 *  non-numeric input passes through untouched. */
export function clampMoneyToWire(raw: string): string {
    if (raw.trim() === "") return raw;
    const n = Number(raw.replace(/,/g, ""));
    if (!Number.isFinite(n)) return raw;
    const clamped = Math.min(999999999999.99, Math.max(0, n));
    return clamped === n ? raw : String(clamped);
}
